import { useEffect, useMemo, useState } from "react";

import type { KpiEntry } from "@/api/kpi";
import { useKpiItems, useKpiSnapshot } from "@/api/kpi";
import { SortableTable } from "@/components/SortableTable";
import { Button, ModalShell, ModalTabs } from "@/components/ui";
import { applyPeriodFilter, queueDetailRows } from "@/features/kpi/periodFilter";
import { useKpiPeriod } from "@/hooks/useKpiPeriod";
import { t } from "@/i18n";
import { fmtInt } from "@/lib/utils";

import { columnsFor, rowClassFor } from "./columns";
import type { KargoSirketiOge } from "./KargoSirketiGrid";
import { groupStockByProduct } from "./stockGroup";

interface KpiDetailModalProps {
  open: boolean;
  kpiId: string | null;
  title: string;
  onClose: () => void;
  /** Modal AI analiz dugmesi — gorunen satirlari (modal_rows) AI'ya bagliyor. */
  onAnalyze?: (rows: KpiEntry[]) => void;
}

type Tab = "processed" | "raw";

/**
 * Kart tiklamasi → detay modali: SortableTable + `/kpi/{kpi_id}/items`;
 * kolonlar `columns.tsx`'ten. Iki ozel sekil:
 *  - `kargo_sirketi`: "İşlenmiş" görünümü `useKpiSnapshot` + `applyPeriodFilter`
 *    ile dönem filtresinden geçmiş `kargo_sirketi.by_cargo_company`'yi kullanır
 *    (backend items yanıtı dönemden bağımsızdır); items yalnız "Ham Veri"de.
 *  - `kritik_stok`: varyant satirlari client-side urun kartina gruplanir.
 */
export function KpiDetailModal({ open, kpiId, title, onClose, onAnalyze }: KpiDetailModalProps) {
  const itemsQuery = useKpiItems(open ? kpiId : null);
  const [tab, setTab] = useState<Tab>("processed");

  const isKargoSirketi = open && kpiId === "kargo_sirketi";
  const snapshotQuery = useKpiSnapshot();
  const { kpiPeriod } = useKpiPeriod();
  const kargoSirketiRows = useMemo<KargoSirketiOge[] | undefined>(() => {
    if (!isKargoSirketi) return undefined;
    const kpis = snapshotQuery.data?.kpis;
    if (!kpis) return undefined;
    const entry = applyPeriodFilter(kpis, kpiPeriod).kpis.kargo_sirketi;
    return Array.isArray(entry?.by_cargo_company) ? (entry!.by_cargo_company as KargoSirketiOge[]) : undefined;
  }, [isKargoSirketi, snapshotQuery.data, kpiPeriod]);

  // Modal her açılışında/farklı bir KPI için "İşlenmiş" sekmesinden başlar.
  useEffect(() => {
    if (open) setTab("processed");
  }, [open, kpiId]);

  if (!open || !kpiId) return null;

  const sourceData = itemsQuery.data;
  const rowKpiId = kpiId === "kargo_sirketi" ? "kargo_yolda" : kpiId;
  const data = sourceData ? { ...sourceData, rows: queueDetailRows(rowKpiId, sourceData.rows, snapshotQuery.data?.kpis, kpiPeriod), items: queueDetailRows(rowKpiId, sourceData.items, snapshotQuery.data?.kpis, kpiPeriod) } : undefined;
  const analysisRows = data ? (isKargoSirketi && tab === "processed" ? (kargoSirketiRows ?? []).map(row => ({ ...row })) : visibleRowsFor(kpiId, data)) : [];
  const satirSayisi = data ? analysisRows.length : null;

  return (
    <ModalShell
      open
      title={satirSayisi != null ? `${title} · ${fmtInt(satirSayisi)}` : title}
      onClose={onClose}
      size="xl"
      testId="kpi-detail-modal"
      actions={
        onAnalyze && data ? (
          <Button size="sm" variant="primary" onClick={() => onAnalyze(analysisRows)}>
            {t("card.analyze")}
          </Button>
        ) : undefined
      }
      tabs={
        data ? (
          <ModalTabs<Tab>
            value={tab}
            onChange={setTab}
            ariaLabel={t("ozet.detay.tab_aria")}
            options={[
              { value: "processed", label: t("ozet.detay.tab_islenmis") },
              { value: "raw", label: t("ozet.detay.tab_ham_veri") },
            ]}
          />
        ) : undefined
      }
    >
      {itemsQuery.isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
      {itemsQuery.isError && (
        <p role="alert" className="text-sm text-red">
          {t("common.error")}
        </p>
      )}
      {data &&
        (tab === "processed" ? (
          <KpiDetailBody kpiId={kpiId} data={data} kargoSirketiRows={kargoSirketiRows} />
        ) : (
          <KpiRawBody data={data} />
        ))}
    </ModalShell>
  );
}

/** Destek talepleri modali — yalniz ACIK biletler (kartin sayaciyla tutarli). */
function isOpenTicket(r: KpiEntry): boolean {
  const sc = r.status_code;
  return sc == null || Number(sc) === 1;
}

/** Modalda GERCEKTEN gosterilen satirlar — tablo ve 'Analiz Et' ayni kaynaktan okur. */
function visibleRowsFor(kpiId: string, data: { rows: KpiEntry[] }): KpiEntry[] {
  return kpiId === "destek_talepleri" ? data.rows.filter(isOpenTicket) : data.rows;
}

/** Satirlarda ortak bir sayisal id yok — birden fazla alani birlestirip deterministik anahtar uretir. */
function rowKeyFor(r: KpiEntry): string {
  const id = r.order_no ?? r.ticket_id ?? r.return_id ?? r.review_id ?? r.date ?? r.hour ?? "";
  const secondary = r.created_at ?? r.request_date ?? "";
  return `${String(id)}|${String(secondary)}`;
}

function KpiDetailBody({
  kpiId,
  data,
  kargoSirketiRows,
}: {
  kpiId: string;
  data: { rows: KpiEntry[]; by_cargo_company: { cargo_company: string; count: number }[]; count: number };
  kargoSirketiRows?: KargoSirketiOge[];
}) {
  if (kpiId === "kargo_sirketi") {
    const rows = kargoSirketiRows ?? [];
    if (rows.length === 0) return <p className="text-sm text-muted">{t("table.empty")}</p>;
    return (
      <SortableTable
        columns={[
          { key: "company", header: t("ozet.detay.kargo_sirketi"), accessor: (r) => r.company },
          { key: "total", header: t("ozet.detay.adet"), accessor: (r) => fmtInt(r.total), sortValue: (r) => r.total },
        ]}
        rows={rows}
        rowKey={(r) => r.company}
        initialSortKey="total"
      />
    );
  }

  if (kpiId === "kritik_stok") {
    const groups = groupStockByProduct(data.rows);
    const columns = columnsFor("kritik_stok")!;
    if (groups.length === 0) return <p className="text-sm text-muted">{t("table.empty")}</p>;
    return (
      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.productCardId}>
            <h3 className="mb-1 font-display text-sm font-bold text-ink">
              {g.name} <span className="font-sans font-normal text-muted">— {fmtInt(g.totalStock)} {t("ozet.detay.toplam_stok")}</span>
            </h3>
            <SortableTable columns={columns} rows={g.variants} rowKey={(r) => `${g.productCardId}-${String(r.sku ?? r.beden ?? r.varyasyon ?? "")}`} />
          </div>
        ))}
      </div>
    );
  }

  const rows = visibleRowsFor(kpiId, data);
  const columns = columnsFor(kpiId);
  if (!columns) return <p className="text-sm text-muted">{t("ozet.detay.kolon_yok")}</p>;
  if (rows.length === 0) return <p className="text-sm text-muted">{t("table.empty")}</p>;
  return <SortableTable columns={columns} rows={rows} rowKey={rowKeyFor} rowClassName={rowClassFor(kpiId)} pageSize={50} />;
}

const RAW_MAX_ROWS = 500;

/** "Ham Veri" sekmesi — satirlarin TUM alanlari (anahtar → deger tablosu). */
function KpiRawBody({ data }: { data: { items: KpiEntry[]; rows: KpiEntry[] } }) {
  const dataset = data.items?.length ? data.items : (data.rows ?? []);
  if (dataset.length === 0) return <p className="text-sm text-muted">{t("ozet.detay.ham_veri_yok")}</p>;

  const cols = Object.keys(dataset[0]).filter((k) => k !== "raw_cells");
  const rows = dataset.slice(0, RAW_MAX_ROWS);

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border-[1.5px] border-line">
        <table className="tbl min-w-[480px] text-xs">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c} scope="col">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="align-top">
                {cols.map((c) => {
                  const v = row[c];
                  const text = v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v);
                  return (
                    <td key={c} className="whitespace-pre-wrap break-all">
                      {text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dataset.length > RAW_MAX_ROWS && (
        <p className="mt-1 text-xs text-muted">{t("ozet.detay.ham_veri_kesildi", { n: String(RAW_MAX_ROWS), toplam: String(dataset.length) })}</p>
      )}
    </div>
  );
}
